import { useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getIntegrationConfigs,
  deleteIntegrationConfig,
  PROVIDER_TYPES,
  type IntegrationConfig,
  type IntegrationConfigFilters,
} from '@/api/integrationApi';
import { useAuth } from '@/hooks/useAuth';
import IntegrationNav from './IntegrationNav';

export default function IntegrationConfigListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [providerTypeFilter, setProviderTypeFilter] = useState<string | undefined>();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: IntegrationConfigFilters = { ...params };
      if (providerTypeFilter) filters.provider_type = providerTypeFilter;
      return getIntegrationConfigs(filters);
    },
    [providerTypeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('integration.confirm_delete_config'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteIntegrationConfig(id);
            message.success(t('integration.config_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<IntegrationConfig> = useMemo(
    () => [
      {
        title: t('integration.field.provider_name'),
        dataIndex: 'provider_name',
        key: 'provider_name',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('integration.field.provider_type'),
        dataIndex: 'provider_type',
        key: 'provider_type',
        width: 120,
        render: (v: string) => (
          <Tag color="blue">{t(`integration.provider_type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('integration.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'default'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('integration.field.scopes'),
        dataIndex: 'scopes',
        key: 'scopes',
        width: 200,
        ellipsis: true,
        render: (v: string[]) => v?.join(', ') || '-',
      },
      {
        title: t('integration.field.redirect_uri'),
        dataIndex: 'redirect_uri',
        key: 'redirect_uri',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 120,
        render: (_: unknown, record: IntegrationConfig) => (
          <Space size="small">
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/integration/configs/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('integration.field.provider_type')}
        value={providerTypeFilter}
        onChange={(v) => { setProviderTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 160 }}
        options={PROVIDER_TYPES.map((pt) => ({
          value: pt,
          label: t(`integration.provider_type.${pt}`),
        }))}
      />
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/integration/configs/new')}
        >
          {t('integration.create_config_title')}
        </Button>
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <IntegrationNav />
      <h2>{t('integration.configs_title')}</h2>
      <DataGrid<IntegrationConfig>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('integration.search_configs_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
